import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { X, Loader2 } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { parseCoords } from "@/lib/coords";

const STATUSES = ["Published", "Hidden", "Retired"];

const inputCls =
  "w-full rounded-[10px] border border-[#deded9] px-3 py-2.5 text-[14px] outline-none focus:border-[#171717] transition-colors disabled:bg-[#f3f2ee] disabled:text-[#9b9a95]";
const labelCls = "text-[10px] tracking-[0.14em] uppercase font-black text-[#6d6d6a] mb-1.5 block";

function Field({ label, hint = null, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[#6d6d6a]">{hint}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] tracking-[0.16em] uppercase font-black text-[#171717] pb-1.5 border-b border-[#ededed]">
        {title}
      </div>
      {children}
    </div>
  );
}

const list = (value) =>
  String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const num = (value) => (String(value ?? "").trim() === "" ? undefined : Number(value));

/**
 * The edit screen behind a bookable partner experience. The partner's own copy
 * stays editable here so an editor can fix a title, a price or a meeting point
 * without reaching for the sync — and the next sync only refreshes what the
 * partner actually sends.
 */
export default function ViatorEditModal({ experience, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: experience.title || "",
    category: experience.category || "",
    subcategory: experience.subcategory || "",
    destination: experience.destination || "",
    theme: experience.theme || "",
    short_description: experience.short_description || "",
    description: experience.description || "",
    duration_text: experience.duration_text || "",
    price_from: experience.price_from ?? "",
    currency: experience.currency || "USD",
    rating: experience.rating ?? "",
    review_count: experience.review_count ?? "",
    booking_url: experience.booking_url || "",
    meeting_point: experience.meeting_point || "",
    cancellation_text: experience.cancellation_text || "",
    included: Array.isArray(experience.included) ? experience.included.join(", ") : experience.included || "",
    neighborhood_id: experience.neighborhood_id || "",
    images: experience.images || [],
    status: experience.status || "Published",
    featured: !!experience.featured,
  });
  const [coordsText, setCoordsText] = useState(
    experience.latitude != null && experience.longitude != null
      ? `${experience.latitude}, ${experience.longitude}`
      : ""
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildPayload = () => {
    const payload = {
      ...form,
      included: list(form.included),
      price_from: num(form.price_from),
      rating: num(form.rating),
      review_count: num(form.review_count),
    };
    const coords = parseCoords(coordsText);
    if (coords) {
      payload.latitude = coords.lat;
      payload.longitude = coords.lng;
    }
    return payload;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const updated = await base44.entities.ViatorExperience.update(experience.id, buildPayload());
      onSaved(updated);
      onClose();
    } catch (e2) {
      setErr(e2.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-[640px] sm:rounded-[20px] rounded-t-[20px] max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#ededed] shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.16em] uppercase font-black text-[#6d6d6a]">
              Edit experience
            </div>
            <div className="text-[18px] font-extrabold tracking-[-0.03em] truncate max-w-[420px]">
              {experience.title}
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f3f2ee] grid place-items-center shrink-0" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={save} className="overflow-y-auto px-5 py-5 space-y-7">
          <Section title="Basics">
            <Field label="Title">
              <input className={inputCls} value={form.title} onChange={set("title")} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <input className={inputCls} value={form.category} onChange={set("category")} />
              </Field>
              <Field label="Subcategory">
                <input className={inputCls} value={form.subcategory} onChange={set("subcategory")} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Destination" hint="Shown under the title on the card.">
                <input className={inputCls} value={form.destination} onChange={set("destination")} />
              </Field>
              <Field label="Theme">
                <input className={inputCls} value={form.theme} onChange={set("theme")} />
              </Field>
            </div>
            <Field label="Short description">
              <input className={inputCls} value={form.short_description} onChange={set("short_description")} />
            </Field>
            <Field label="Description">
              <textarea rows={5} className={inputCls} value={form.description} onChange={set("description")} />
            </Field>
          </Section>

          <Section title="Booking">
            <Field label="Booking link" hint="Where the booking is finished — the partner's own page.">
              <input className={inputCls} value={form.booking_url} onChange={set("booking_url")} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Price from">
                <input className={inputCls} value={form.price_from} onChange={set("price_from")} inputMode="decimal" />
              </Field>
              <Field label="Currency">
                <input className={inputCls} value={form.currency} onChange={set("currency")} />
              </Field>
              <Field label="Duration">
                <input className={inputCls} value={form.duration_text} onChange={set("duration_text")} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Rating">
                <input className={inputCls} value={form.rating} onChange={set("rating")} inputMode="decimal" />
              </Field>
              <Field label="Reviews">
                <input className={inputCls} value={form.review_count} onChange={set("review_count")} inputMode="numeric" />
              </Field>
            </div>
            <Field label="What's included" hint="Separate each item with a comma.">
              <input className={inputCls} value={form.included} onChange={set("included")} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Meeting point">
                <input className={inputCls} value={form.meeting_point} onChange={set("meeting_point")} />
              </Field>
              <Field label="Cancellation">
                <input className={inputCls} value={form.cancellation_text} onChange={set("cancellation_text")} />
              </Field>
            </div>
          </Section>

          <Section title="Photos">
            <Field label="Gallery" hint="The first photo leads the card.">
              <ImageUpload multiple value={form.images} onChange={(v) => setForm({ ...form, images: v })} />
            </Field>
          </Section>

          <Section title="Place">
            <Field label="Neighborhood">
              <input className={inputCls} value={form.neighborhood_id} onChange={set("neighborhood_id")} />
            </Field>
            <Field label="Coordinates" hint="Paste a latitude, longitude pair or a Google Maps link.">
              <input
                className={inputCls}
                value={coordsText}
                onChange={(e) => setCoordsText(e.target.value)}
                placeholder="18.4655, -66.1057"
              />
            </Field>
          </Section>

          <Section title="Publishing">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={set("status")}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Featured">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, featured: !form.featured })}
                  className={`w-full rounded-[10px] border px-3 py-2.5 text-[14px] font-bold transition-colors ${
                    form.featured
                      ? "border-[#171717] bg-[#171717] text-white"
                      : "border-[#deded9] text-[#3f3f3c]"
                  }`}
                >
                  {form.featured ? "Featured" : "Not featured"}
                </button>
              </Field>
            </div>
          </Section>

          {err && (
            <div className="rounded-[10px] border border-[#F97316] bg-[#FFF7ED] px-3.5 py-2.5 text-[12.5px] font-medium text-[#7c2d12]">
              {err}
            </div>
          )}

          <div className="flex items-center gap-3 pb-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#171717] text-white px-5 py-3 text-[12px] font-bold font-mono uppercase tracking-wider inline-flex items-center justify-center gap-2 border border-[#171717] hover:bg-[#3f3f3c] transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 text-[12px] font-bold font-mono uppercase tracking-wider border border-[#deded9] text-[#3f3f3c]"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}